import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, CheckCircle2, Star, Quote, Plus, ChevronLeft, ChevronRight, ShieldCheck, Clock3, Globe2, Users2 } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/Seo";
import { Icon } from "@/lib/icons";
import { IMG, AVATARS } from "@/lib/images";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { SectionHeader } from "@/components/SectionHeader";
import { PillButton } from "@/components/PillButton";
import { CtaBand } from "@/components/CtaBand";
import { CardsSkeleton, ErrorState } from "@/components/States";
import { useSettings, useServices, useProducts, useIndustries, useTestimonials, useFaqs } from "@/hooks/useSiteData";

const MARQUEE = ["Future-proof your business today", "Smarter strategies. Better results.", "Let's transform the way you work", "Enterprise platforms, engineered since 2010", "Consulting · Cloud · Mobile · AI"];

const STEPS = [
  { n: "01", t: "Deep Discovery", d: "Stakeholder interviews, current-state audit and success metrics.", img: IMG.discussion },
  { n: "02", t: "Strategic Planning", d: "Scope, architecture, roadmap and a costed proposal.", img: IMG.planning },
  { n: "03", t: "Design & Validate", d: "UX research, prototypes and design systems validated with users.", img: IMG.collaboration },
  { n: "04", t: "Seamless Execution", d: "Agile sprints, CI/CD, QA automation and transparent reporting.", img: IMG.developers },
  { n: "05", t: "Optimisation", d: "Managed services, analytics and continuous improvement.", img: IMG.analytics },
];

const FEATURES = [
  { Icon: Clock3, t: "15+ years of enterprise delivery", d: "Bootstrapped in 2010 and grown by keeping clients for the long term." },
  { Icon: Globe2, t: "India + US footprint", d: "Delivery hubs in Bengaluru and Noida (Delhi-NCR); clients across the United States." },
  { Icon: ShieldCheck, t: "Secure by design", d: "Encryption, access control and audit trails built into every platform." },
  { Icon: Users2, t: "Senior, accountable teams", d: "Work directly with architects and leads - never a rotating bench." },
];

const SERVICE_PHOTOS = [IMG.strategy, IMG.callcenter, IMG.workshop, IMG.laptops, IMG.glasses, IMG.manLaptop, IMG.engineer, IMG.dashboard];

export default function HomePage() {
  const { data: settings } = useSettings();
  const services = useServices();
  const products = useProducts();
  const industries = useIndustries();
  const testimonials = useTestimonials();
  const faqs = useFaqs();
  const [activeService, setActiveService] = useState(0);
  const [tIndex, setTIndex] = useState(0);

  const svc = services.data?.[activeService];
  const tList = testimonials.data || [];
  const t = tList[tIndex % Math.max(tList.length, 1)];
  const stats = settings?.stats || [];

  return (
    <>
      <Seo path="/" />

      {/* ================= HERO ================= */}
      <section className="ink-pattern relative overflow-hidden" data-testid="home-hero">
        <div className="container-x relative pt-14 sm:pt-20 lg:pt-24">
          <div className="grid items-end gap-10 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <span className="eyebrow eyebrow-light">Trusted technology partner since 2010</span>
              <h1 className="section-title-lg mt-5 text-white" data-testid="hero-title">{settings?.hero_title || "Enterprise digital experiences, engineered since 2010."}</h1>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-5 lg:pb-3">
              <p className="text-base leading-relaxed text-white/70 sm:text-lg" data-testid="hero-subtitle">{settings?.hero_subtitle}</p>
              <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-5">
                <PillButton to="/contact" variant="primary" size="lg" testId="hero-primary-cta-button">Get a Free Consultation</PillButton>
                <div className="flex items-center gap-3">
                  <div className="flex -space-x-2.5">{AVATARS.map((a, i) => <img key={i} src={a} alt="" className="h-10 w-10 rounded-full border-2 border-[#0e1013] object-cover" />)}<span className="inline-flex h-10 w-10 items-center justify-center rounded-full border-2 border-[#0e1013] bg-primary text-xs font-semibold">50+</span></div>
                  <div className="whitespace-nowrap text-xs leading-tight text-white/70"><span className="flex text-primary">{[1, 2, 3, 4, 5].map((i) => <Star key={i} className="h-3 w-3 fill-current" />)}</span>Rated by enterprise clients</div>
                </div>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2} className="relative mt-12 lg:mt-16">
            <div className="photo aspect-[16/9] rounded-3xl sm:aspect-[21/9]">
              <img src={IMG.heroWide} alt="Jonaren Technology team collaborating" loading="eager" />
            </div>
            <div className="absolute bottom-5 left-5 hidden items-center gap-4 rounded-2xl bg-white p-4 text-foreground shadow-2xl sm:flex" data-testid="hero-metric-card">
              <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary"><Clock3 className="h-6 w-6" /></div>
              <div className="flex items-baseline gap-2 whitespace-nowrap"><p className="font-heading text-2xl font-medium leading-none">{stats[0]?.value || "15+"}</p><p className="text-sm font-medium text-foreground/80">{stats[0]?.label || "Years of delivery"}</p></div>
            </div>
          </Reveal>
        </div>

        {/* Marquee */}
        <div className="container-x mt-12">
          <div className="marquee border-y border-white/10 py-5" aria-hidden="true" data-testid="hero-marquee">
            {[0, 1].map((k) => (
              <div key={k} className="marquee-track">
                {MARQUEE.map((m) => <span key={m} className="inline-flex items-center gap-10 whitespace-nowrap font-heading text-lg text-white/90 sm:text-xl"><ArrowRight className="h-5 w-5 text-primary" />{m}</span>)}
              </div>
            ))}
          </div>
        </div>

        {/* Stats */}
        <div className="container-x">
          <div className="stat-divider grid grid-cols-2 py-12 sm:grid-cols-4 lg:py-16" data-testid="trust-strip">
            {stats.map((s, i) => (
              <Reveal key={s.label} delay={i * 0.06} className="relative px-2 py-4 text-center sm:px-6">
                <span className="absolute right-3 top-2 h-1.5 w-1.5 rounded-full bg-primary" aria-hidden="true" />
                <p className="font-heading text-4xl font-medium leading-none sm:text-5xl">{s.value}</p>
                <p className="mx-auto mt-3 max-w-[12rem] text-sm text-white/60">{s.label}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ================= SERVICES (tabs) ================= */}
      <section className="section paper" id="services" data-testid="home-services-section">
        <div className="container-x">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-6"><SectionHeader eyebrow="Explore our services" title="What we are offering to our potential clients" /></div>
            <Reveal className="flex flex-col gap-5 sm:flex-row sm:items-center lg:col-span-6 lg:justify-end">
              <p className="max-w-sm text-sm text-muted-foreground">Eight practices working as one accountable team - strategy, design, engineering and operations for enterprise digital platforms.</p>
              <PillButton to="/services" variant="dark" testId="home-view-all-services-button">View all services</PillButton>
            </Reveal>
          </div>

          {services.isLoading && <CardsSkeleton className="mt-12" count={3} />}
          {services.isError && <div className="mt-12"><ErrorState onRetry={services.refetch} /></div>}
          {services.data && (
            <Reveal className="mt-12">
              <div className="flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist" aria-label="Services">
                {services.data.map((s, i) => (
                  <button key={s.id} role="tab" aria-selected={i === activeService} onClick={() => setActiveService(i)} className={`shrink-0 rounded-full px-5 py-2.5 text-sm font-medium transition-[background-color,color,border-color] duration-200 ${i === activeService ? "bg-[#0e1013] text-white" : "border border-border bg-white text-foreground/70 hover:border-foreground hover:text-foreground"}`} data-testid={`services-tab-${s.slug}`}>{s.title}</button>
                ))}
              </div>

              {svc && (
                <div className="mt-8 grid gap-8 rounded-3xl bg-white p-6 sm:p-8 lg:grid-cols-12 lg:gap-12 lg:p-12" data-testid={`services-panel-${svc.slug}`} key={svc.id}>
                  <div className="flex flex-col lg:col-span-5">
                    <span className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-[#0e1013] text-white"><Icon name={svc.icon} className="h-6 w-6" /></span>
                    <h3 className="mt-6 font-heading text-3xl font-medium leading-tight">{svc.title}</h3>
                    <p className="mt-4 text-muted-foreground">{svc.description}</p>
                    <ul className="mt-6 grid gap-2.5">{svc.capabilities.slice(0, 4).map((c) => <li key={c} className="flex items-start gap-2.5 text-sm font-medium"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />{c}</li>)}</ul>
                    <div className="mt-8"><PillButton to={`/services/${svc.slug}`} variant="dark" testId={`services-grid-card-${svc.slug}`}>Service details</PillButton></div>
                  </div>
                  <div className="relative lg:col-span-7">
                    <div className="grid grid-cols-12 gap-4">
                      <div className="photo col-span-8 aspect-[4/3]"><img src={svc.image} alt={svc.title} loading="lazy" /></div>
                      <div className="col-span-4 flex flex-col gap-4">
                        <div className="photo aspect-square"><img src={SERVICE_PHOTOS[activeService % SERVICE_PHOTOS.length]} alt="" loading="lazy" /></div>
                        <div className="flex flex-1 flex-col justify-center rounded-2xl bg-[#0e1013] p-4 text-white"><p className="font-heading text-2xl font-medium leading-none sm:text-3xl">{svc.outcomes.length}+</p><p className="mt-1 text-[11px] leading-tight text-white/60">Measurable outcomes per engagement</p></div>
                      </div>
                    </div>
                    <div className="mt-4 flex flex-wrap gap-2">{svc.outcomes.map((o) => <span key={o} className="rounded-full border border-border bg-[#f6f6f4] px-3.5 py-1.5 text-xs font-medium">{o}</span>)}</div>
                  </div>
                </div>
              )}
            </Reveal>
          )}
        </div>
      </section>

      {/* ================= ABOUT ================= */}
      <section className="section bg-white" data-testid="home-about-section">
        <div className="container-x">
          <Reveal className="relative">
            <div className="photo aspect-[16/9] rounded-3xl sm:aspect-[21/9]"><img src={IMG.teamTable} alt="Team working together" loading="lazy" /></div>
            <div className="mt-4 flex justify-end sm:absolute sm:-bottom-8 sm:right-8 sm:mt-0 lg:right-12">
              <div className="flex w-full items-center gap-5 rounded-2xl bg-[#0e1013] px-7 py-5 text-white shadow-2xl sm:w-auto sm:min-w-[360px]" data-testid="about-years-card"><p className="font-heading text-5xl font-medium leading-none">{stats[0]?.value || "15+"}</p><p className="text-sm leading-snug text-white/70">Years of experience<br />building enterprise platforms</p></div>
            </div>
          </Reveal>

          <div className="paper mt-4 grid gap-10 rounded-3xl p-7 sm:mt-4 sm:p-10 sm:pt-16 lg:grid-cols-12 lg:p-14 lg:pt-14">
            <Reveal className="lg:col-span-6">
              <span className="eyebrow">About us</span>
              <h2 className="section-title mt-4">Choose trusted experts with a proven track record to elevate your business.</h2>
              <p className="mt-5 text-muted-foreground">Jonaren Technology was founded in {settings?.founded_year || 2010} on a simple belief: enterprises deserve technology partners who understand their business first and their technology second. Fifteen years on, that belief still shapes every engagement.</p>
              <div className="mt-8"><PillButton to="/about" variant="dark" testId="home-about-button">Discover more</PillButton></div>
            </Reveal>
            <div className="lg:col-span-6">
              <Reveal delay={0.05} className="flex gap-6 border-b border-border pb-8">
                <span className="vertical-text shrink-0 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Our mission</span>
                <p className="text-[15px] leading-relaxed text-muted-foreground">{settings?.mission}</p>
              </Reveal>
              <Reveal delay={0.1} className="flex gap-6 pt-8">
                <span className="vertical-text shrink-0 text-[11px] font-semibold uppercase tracking-[0.2em] text-primary">Our vision</span>
                <p className="text-[15px] leading-relaxed text-muted-foreground">{settings?.vision}</p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="paper pb-16 sm:pb-20 lg:pb-28" data-testid="home-why-section">
        <div className="container-x">
          <SectionHeader eyebrow="Why JNT" title="Our exciting and powerful capabilities" align="center" />
          <Stagger className="mt-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
            {FEATURES.map((f) => (
              <StaggerItem key={f.t}>
                <div className="group" data-testid={`feature-${f.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="inline-flex h-16 w-16 items-center justify-center rounded-full border border-border bg-white text-foreground transition-colors duration-300 group-hover:border-primary group-hover:bg-primary group-hover:text-white"><f.Icon className="h-7 w-7" strokeWidth={1.5} /></span>
                  <h3 className="mt-6 font-heading text-xl font-medium">{f.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{f.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      {/* ================= PRODUCTS (photo grid) ================= */}
      <section className="section ink-pattern" id="products" data-testid="home-products-section">
        <div className="container-x">
          <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-7"><SectionHeader light eyebrow="Our products" title="Selected products that reflect our expertise" lead="Each JNT product started as a client engagement and matured into a configurable platform you can deploy in weeks." /></div>
            <Reveal className="lg:col-span-5 lg:flex lg:justify-end"><PillButton to="/products" variant="primary" testId="home-view-all-products-button">View all products</PillButton></Reveal>
          </div>
          {products.isLoading && <CardsSkeleton className="mt-12" count={3} />}
          {products.data && (
            <Stagger className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-6">
              {products.data.map((p, i) => (
                <StaggerItem key={p.id} className={i < 2 ? "lg:col-span-3" : "lg:col-span-2"}>
                  <Link to={`/products/${p.slug}`} className="group relative block overflow-hidden rounded-3xl" data-testid={`products-tab-${p.slug}`}>
                    <div className={`photo rounded-3xl ${i < 2 ? "aspect-[16/10]" : "aspect-[4/3]"}`}><img src={p.image} alt={p.name} loading="lazy" /></div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0e1013] via-[#0e1013]/30 to-transparent" aria-hidden="true" />
                    <div className="absolute inset-x-0 bottom-0 flex items-end justify-between gap-4 p-6">
                      <div><span className="inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-primary"><Icon name={p.icon} className="h-3.5 w-3.5" />{p.featured ? "Flagship" : "Product"}</span><h3 className="mt-2 font-heading text-2xl font-medium leading-tight">{p.name}</h3><p className="mt-1 text-sm text-white/70">{p.tagline}</p></div>
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-white text-[#0e1013] transition-transform duration-300 group-hover:rotate-45"><ArrowUpRight className="h-5 w-5" /></span>
                    </div>
                  </Link>
                </StaggerItem>
              ))}
            </Stagger>
          )}
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="section paper" data-testid="home-process-section">
        <div className="container-x">
          <SectionHeader eyebrow="Working process" title="Shaping the future through step-by-step innovation" align="center" />
          <Stagger className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
            {STEPS.map((s) => (
              <StaggerItem key={s.n}>
                <div className="group relative flex h-full flex-col items-center rounded-3xl border border-border bg-white px-5 pb-7 pt-9 text-center card-hover" data-testid={`process-step-${s.t.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <span className="step-pill absolute -top-3">Step {s.n}</span>
                  <div className="h-28 w-28 overflow-hidden rounded-full ring-4 ring-[#f6f6f4]"><img src={s.img} alt="" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" /></div>
                  <h3 className="mt-6 font-heading text-lg font-medium">{s.t}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.d}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal className="mt-10 text-center text-sm text-muted-foreground">Ready to start? Let&apos;s build something great together. <Link to="/contact" className="font-semibold text-foreground underline decoration-primary decoration-2 underline-offset-4 hover:text-primary" data-testid="process-contact-link">Get in touch</Link></Reveal>
        </div>
      </section>

      {/* ================= STRONG SINCE 2010 (split) ================= */}
      <section className="relative bg-white" data-testid="home-strong-section">
        <div className="grid lg:grid-cols-12">
          <div className="relative min-h-[320px] lg:col-span-9 lg:min-h-[520px]"><img src={IMG.workshop} alt="" className="absolute inset-0 h-full w-full object-cover grayscale" loading="lazy" /></div>
          <div className="ink flex items-center justify-center px-8 py-12 lg:col-span-3">
            <div className="flex items-center gap-5 lg:rotate-180 lg:[writing-mode:vertical-rl]">
              <p className="font-heading text-5xl font-medium leading-none lg:text-6xl">{settings?.founded_year || 2010}</p>
              <p className="text-sm text-white/60">Founded in Bengaluru.<br />Bootstrapped. Profitable.</p>
            </div>
          </div>
        </div>
        <div className="container-x relative -mt-20 pb-20 lg:-mt-28 lg:pb-28">
          <Reveal className="grid gap-8 rounded-3xl bg-white p-6 shadow-2xl lg:grid-cols-12 lg:p-10">
            <div className="lg:col-span-3">
              <div className="photo aspect-[3/4]"><img src="/assets/director-3.webp" alt="Biprajit Biswas, Director of Jonaren Technology" className="object-top" loading="lazy" data-testid="director-portrait" /></div>
              <div className="mt-4 flex items-center gap-3" data-testid="director-caption">
                <span className="h-8 w-1 rounded-full bg-primary" aria-hidden="true" />
                <div><p className="font-heading text-lg font-medium leading-tight">Biprajit Biswas</p><p className="text-sm text-muted-foreground">Director, Jonaren Technology</p></div>
              </div>
            </div>
            <div className="lg:col-span-4">
              <span className="eyebrow">Our promise to every client</span>
              <h3 className="mt-4 font-heading text-3xl font-medium leading-tight">15 years strong, growing with you.</h3>
              <ul className="mt-6 space-y-4">
                {(settings?.values || []).slice(0, 2).map((v) => <li key={v.title} className="flex gap-3"><CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" /><div><p className="font-semibold">{v.title}</p><p className="text-sm text-muted-foreground">{v.text}</p></div></li>)}
              </ul>
            </div>
            <div className="relative lg:col-span-5 lg:border-l lg:border-border lg:pl-10">
              <Quote className="h-10 w-10 text-primary" />
              {tList[0] && <><blockquote className="mt-4 text-lg leading-relaxed text-foreground/85">&ldquo;{tList[0].quote}&rdquo;</blockquote><p className="mt-5 font-semibold">{tList[0].name} <span className="font-normal text-muted-foreground">- {tList[0].role}{tList[0].company ? `, ${tList[0].company}` : ""}</span></p></>}
              <img src="/assets/logo_badge.png" alt="" className="absolute bottom-0 right-0 hidden h-12 w-auto opacity-20 lg:block" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* ================= INDUSTRIES (tag cloud) ================= */}
      <section className="section ink-pattern" data-testid="home-industries-section">
        <div className="container-x text-center">
          <SectionHeader light align="center" eyebrow="Industries we serve" title="Expert technology solutions for every industry" />
          {industries.data && (
            <Reveal className="mx-auto mt-10 flex max-w-4xl flex-wrap justify-center gap-3">
              {industries.data.map((ind) => <Link key={ind.id} to={`/industries#${ind.slug}`} className="tag-pill" data-testid={`industry-card-${ind.slug}`}><Icon name={ind.icon} className="mr-2 h-4 w-4 opacity-70" />{ind.name}</Link>)}
              {["Healthcare", "Logistics & Supply Chain", "Professional Services", "Real Estate", "Public Sector"].filter((x) => !industries.data.some((i) => i.name === x)).map((x) => <span key={x} className="tag-pill cursor-default">{x}</span>)}
            </Reveal>
          )}
          <Reveal className="mt-10"><PillButton to="/industries" variant="primary" testId="home-view-all-industries-button">See all industries</PillButton></Reveal>
        </div>
      </section>

      {/* ================= TESTIMONIALS ================= */}
      <section className="section paper" data-testid="home-testimonials-section">
        <div className="container-x grid items-center gap-12 lg:grid-cols-12">
          <div className="lg:col-span-6">
            <SectionHeader eyebrow="Testimonials" title="What our happy clients say about us." lead="Our success is measured by the years clients stay with us." />
            {t && (
              <Reveal className="mt-8" key={t.id}>
                <div className="flex gap-0.5 text-primary" aria-label={`${t.rating} out of 5 stars`}>{Array.from({ length: t.rating }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}</div>
                <blockquote className="mt-4 text-lg leading-relaxed text-foreground/85" data-testid={`testimonial-card-${t.id}`}>&ldquo;{t.quote}&rdquo;</blockquote>
                <div className="mt-6 flex items-center justify-between border-t border-border pt-5">
                  <div className="flex items-center gap-3"><span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#0e1013] font-heading text-base font-medium text-white">{t.name[0]}</span><div><p className="font-semibold">{t.name}</p><p className="text-sm text-muted-foreground">{t.role}{t.company ? `, ${t.company}` : ""}</p></div></div>
                  {tList.length > 1 && (
                    <div className="flex gap-2">
                      <button onClick={() => setTIndex((i) => (i - 1 + tList.length) % tList.length)} className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-border bg-white transition-colors hover:border-foreground" aria-label="Previous testimonial" data-testid="testimonial-prev-button"><ChevronLeft className="h-4 w-4" /></button>
                      <button onClick={() => setTIndex((i) => (i + 1) % tList.length)} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0e1013] text-white transition-colors hover:bg-primary" aria-label="Next testimonial" data-testid="testimonial-next-button"><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  )}
                </div>
              </Reveal>
            )}
          </div>
          <Reveal delay={0.1} className="lg:col-span-6"><div className="photo aspect-[4/3] rounded-3xl"><img src={IMG.handshake} alt="Client partnership" loading="lazy" /></div></Reveal>
        </div>
      </section>

      {/* ================= FAQ (split) ================= */}
      <section className="section bg-white" data-testid="home-faq-section">
        <div className="container-x grid gap-5 lg:grid-cols-12">
          <div className="grid gap-5 sm:grid-cols-2 lg:col-span-7">
            <Reveal className="photo aspect-[3/4] rounded-3xl sm:aspect-auto"><img src={IMG.womanLaptop} alt="" loading="lazy" /></Reveal>
            <div className="flex flex-col gap-5">
              <Reveal delay={0.05} className="photo aspect-[4/3] rounded-3xl"><img src={IMG.officeOpen} alt="" loading="lazy" /></Reveal>
              <Reveal delay={0.1} className="flex flex-1 flex-col justify-end rounded-3xl bg-[#0e1013] p-7 text-white">
                <h3 className="font-heading text-2xl font-medium leading-tight">Couldn&apos;t find your answer? Ask us a question.</h3>
                <div className="mt-6"><PillButton to="/contact" variant="primary" size="sm" testId="faq-contact-button">Get in touch</PillButton></div>
              </Reveal>
            </div>
          </div>
          <Reveal className="ink-pattern-soft rounded-3xl p-7 sm:p-10 lg:col-span-5">
            <span className="eyebrow eyebrow-light">Learn more about us</span>
            <h2 className="mt-4 font-heading text-3xl font-medium leading-tight">Frequently asked questions</h2>
            {faqs.data && (
              <Accordion type="single" collapsible className="mt-6 divide-y divide-white/10">
                {faqs.data.map((f, i) => (
                  <AccordionItem key={f.id} value={f.id} className="border-b-0">
                    <AccordionTrigger className="group py-4 text-left text-[15px] font-medium text-white hover:no-underline [&>svg]:hidden" data-testid={`faq-trigger-${f.id}`}>
                      <span className="flex-1 pr-4"><span className="mr-2 text-primary">{i + 1}.</span>{f.question}</span>
                      <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-white/20 transition-transform duration-200 group-data-[state=open]:rotate-45"><Plus className="h-3.5 w-3.5" /></span>
                    </AccordionTrigger>
                    <AccordionContent className="pb-4 text-sm leading-relaxed text-white/65">{f.answer}</AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </Reveal>
        </div>
      </section>

      {/* ================= CONTACT / CTA ================= */}
      <div id="contact"><CtaBand /></div>
    </>
  );
}
